import React from 'react'
import {
  Box,
  Paper,
  Button,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Upload as UploadIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { statusDisplayMap, StudyWorkflowStatus } from '../../constants/studyStatus'

export type DateType = 'study_date' | 'update_date' | 'received_date' | 'report_date'

export interface DoctorOption {
  id: string
  email?: string
  doctor_profile?: {
    first_name?: string
    last_name?: string
  }
}

interface FiltersBarProps {
  // Date filter
  dateType: DateType
  onDateTypeChange: (type: DateType) => void
  dateRange: { start: string; end: string } | null
  onDateRangeChange: (range: { start: string; end: string } | null) => void
  
  // Status filter
  selectedStatuses: string[]
  availableStatuses: string[]
  onStatusToggle: (status: string) => void
  
  // Modality filter
  selectedModalities: string[]
  availableModalities: string[]
  onModalityToggle: (modality: string) => void
  
  // Search
  searchQuery: string
  onSearchChange: (query: string) => void
  
  // Institution filter
  selectedInstitution: string
  availableInstitutions: Array<{ id: string; name: string; is_coordinated?: boolean }>
  onInstitutionChange: (institutionId: string) => void

  // Doctor filter (optional)
  showDoctorFilter?: boolean
  doctorOptions?: DoctorOption[]
  doctorValue?: DoctorOption | null
  doctorInputValue?: string
  loadingDoctorOptions?: boolean
  onDoctorInputChange?: (value: string, reason: 'input' | 'clear' | 'reset') => void
  onDoctorChange?: (value: DoctorOption | null) => void
  
  // Actions
  onRefresh: () => void
  onSync: () => void
  onDownloadSelected: () => void
  onShare?: () => void
  isRefreshing?: boolean
  isSyncing?: boolean
  canSync?: boolean // Permission check for sync button
  canShare?: boolean
  canDownload?: boolean // Permission check for download button
  selectedCount?: number // Number of selected studies
  
  // Quick action buttons
  showQuickActions?: boolean  // Upload DICOM button
  canAddPatient?: boolean     // Add patient button
  onUploadDicom?: () => void
  onAddPatient?: () => void
}

const FiltersBar: React.FC<FiltersBarProps> = ({
  dateType: _dateType,
  onDateTypeChange: _onDateTypeChange,
  dateRange,
  onDateRangeChange,
  selectedStatuses,
  availableStatuses,
  onStatusToggle,
  selectedModalities,
  availableModalities,
  onModalityToggle,
  searchQuery,
  onSearchChange,
  selectedInstitution,
  availableInstitutions,
  onInstitutionChange,
  showDoctorFilter = false,
  doctorOptions = [],
  doctorValue = null,
  doctorInputValue = '',
  loadingDoctorOptions = false,
  onDoctorInputChange,
  onDoctorChange,
  onRefresh,
  onSync,
  onDownloadSelected,
  onShare,
  isRefreshing = false,
  isSyncing = false,
  canSync = false,
  canShare = false,
  canDownload = true,
  selectedCount = 0,
  showQuickActions = false,
  canAddPatient = false,
  onUploadDicom,
  onAddPatient,
}) => {
  const formatDateForInput = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  const parseDateFromInput = (dateStr: string): Date | null => {
    const parts = dateStr.split('.')
    if (parts.length !== 3) return null
    const day = parseInt(parts[0], 10)
    const month = parseInt(parts[1], 10) - 1
    const year = parseInt(parts[2], 10)
    return new Date(year, month, day)
  }

  const _getDateRangeString = (): string => {
    if (!dateRange) return 'DD.MM.YYYY-DD.MM.YYYY'
    return `${formatDateForInput(new Date(dateRange.start))}-${formatDateForInput(new Date(dateRange.end))}`
  }

  const _handlePresetClick = (preset: string) => {
    const today = new Date()
    today.setHours(23, 59, 59, 999)
    let start = new Date()
    start.setHours(0, 0, 0, 0)

    switch (preset) {
      case 'day':
        break
      case '3days':
        start.setDate(today.getDate() - 2)
        break
      case 'week':
        start.setDate(today.getDate() - 6)
        break
      case 'month':
        start.setMonth(today.getMonth() - 1)
        break
      case 'quarter':
        start.setMonth(today.getMonth() - 3)
        break
      case 'year':
        start.setFullYear(today.getFullYear() - 1)
        break
      case 'decade':
        start.setFullYear(today.getFullYear() - 10)
        break
      case '30days':
        start.setDate(today.getDate() - 29)
        break
    }

    onDateRangeChange({
      start: start.toISOString(),
      end: today.toISOString(),
    })
  }

  const _handleDateRangeInputChange = (value: string) => {
    const parts = value.split('-')
    if (parts.length === 2) {
      const startDate = parseDateFromInput(parts[0].trim())
      const endDate = parseDateFromInput(parts[1].trim())
      if (startDate && endDate) {
        startDate.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        onDateRangeChange({
          start: startDate.toISOString(),
          end: endDate.toISOString(),
        })
      }
    }
  }

  const _navigateDateRange = (direction: 'prev' | 'next') => {
    if (!dateRange) return

    const start = new Date(dateRange.start)
    const end = new Date(dateRange.end)
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))

    if (direction === 'prev') {
      start.setDate(start.getDate() - diffDays - 1)
      end.setDate(end.getDate() - diffDays - 1)
    } else {
      start.setDate(start.getDate() + diffDays + 1)
      end.setDate(end.getDate() + diffDays + 1)
    }

    onDateRangeChange({
      start: start.toISOString(),
      end: end.toISOString(),
    })
  }

  // Modalitățile DICOM standard în ordinea din imagine
  const standardModalities = [
    'CT', 'PT', 'MR', 'US', 'CR', 'MG', 'NM',
    'DX', 'OT', 'XA', 'XR', 'XC', 'RF', 'DR',
    'DS', 'SR', 'OCT', 'PX', 'ECG', 'ES', 'IO',
  ]

  const allModalities = [...new Set([...standardModalities, ...availableModalities])].sort()

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        mb: 1.2, 
        p: 0.6,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      {/* Rând 1 - blocuri */}
      <Box
        sx={{
          display: 'grid',
          // IMPORTANT: blocul de Modalități NU trebuie să fie "1fr" (prea lat),
          // altfel nu va face wrap. Îl limităm ca să cadă pe 2 rânduri.
          gridTemplateColumns: {
            xs: '1fr',
            md: showQuickActions 
              ? '240px 64px 260px minmax(280px, 380px) auto'
              : '240px 64px 260px minmax(320px, 420px)',
          },
          gap: 0.6,
          alignItems: 'stretch',
          mb: 0.6,
        }}
      >
        {/* Bloc: Instituție + Medic */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            minHeight: 62,
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Instituție</InputLabel>
            <Select
              value={selectedInstitution}
              onChange={(e) => onInstitutionChange(e.target.value)}
              label="Instituție"
              sx={{
                fontSize: '0.75rem',
                height: 28,
                backgroundColor: '#2d3748',
                color: '#fff',
                '& .MuiSelect-select': { py: 0.25, px: 1 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4a5568' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#718096' },
                '& .MuiSvgIcon-root': { color: '#fff' },
              }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>Toate instituțiile</MenuItem>
              {availableInstitutions.map((institution) => (
                <MenuItem key={institution.id} value={institution.id} sx={{ fontSize: '0.75rem' }}>
                  {institution.is_coordinated && (
                    <span style={{ color: '#f6ad55', marginRight: 6 }} title="Coordonator">★</span>
                  )}
                  {institution.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {showDoctorFilter && (
            <Autocomplete
              size="small"
              options={doctorOptions}
              value={doctorValue}
              inputValue={doctorInputValue}
              onInputChange={(_event, value, reason) => {
                onDoctorInputChange?.(value, reason as any)
              }}
              onChange={(_event, value) => {
                onDoctorChange?.(value as DoctorOption | null)
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option
                const name = `${option.doctor_profile?.first_name || ''} ${option.doctor_profile?.last_name || ''}`.trim()
                return name || option.email || ''
              }}
              loading={loadingDoctorOptions}
              noOptionsText="Nu s-au găsit medici"
              sx={{
                // dropdown list styling
                '& .MuiAutocomplete-paper': {
                  backgroundColor: '#2d3748',
                  color: '#fff',
                },
                '& .MuiAutocomplete-listbox': {
                  fontSize: '0.75rem',
                },
              }}
              renderOption={(props, option) => {
                const name = `${option.doctor_profile?.first_name || ''} ${option.doctor_profile?.last_name || ''}`.trim()
                const label = name || option.email || ''
                return (
                  <li {...props} style={{ fontSize: '0.75rem' }}>
                    {label}
                  </li>
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Medic"
                  placeholder="Caută medic..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingDoctorOptions ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.75rem',
                      color: '#fff',
                    },
                    '& .MuiInputBase-root': {
                      height: 28,
                      backgroundColor: '#2d3748',
                      color: '#fff',
                      fontSize: '0.75rem',
                    },
                    '& .MuiInputBase-input': { py: 0.25, px: 1 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4a5568' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#718096' },
                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#718096' },
                    '& .MuiSvgIcon-root': { color: '#fff' },
                  }}
                />
              )}
            />
          )}
        </Box>

        {/* Bloc: Refresh / Sync */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 62,
          }}
        >
          <Tooltip title="Reîmprospătare">
            <IconButton
              size="small"
              onClick={onRefresh}
              disabled={isRefreshing}
              sx={{
                width: 28,
                height: 28,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title={canSync ? "Sincronizare PACS" : "Nu aveți permisiuni pentru sincronizare"}>
            <span>
              <IconButton
                size="small"
                onClick={onSync}
                disabled={!canSync || isSyncing || isRefreshing}
                sx={{
                  width: 28,
                  height: 28,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&.Mui-disabled': { opacity: 0.5, cursor: 'not-allowed' },
                }}
              >
                <SyncIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Bloc: Status (2 rânduri) */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0.5,
            minHeight: 62,
            overflow: 'hidden',
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 0.25, color: 'text.secondary' }}>
            Status
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)', // 3 coloane egale
              gap: 0.5,
            }}
          >
            {availableStatuses.map((status) => {
              const isSelected = selectedStatuses.includes(status)
              // Folosim statusDisplayMap pentru label și culoare
              const displayInfo = statusDisplayMap[status as StudyWorkflowStatus]
              const statusLabel = displayInfo?.label || status
              const statusColor = displayInfo?.bgColor || '#616161'

              return (
                <Button
                  key={status}
                  size="small"
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={() => onStatusToggle(status)}
                  sx={{
                    minWidth: 'auto',
                    height: 22,
                    px: 0.75,
                    py: 0,
                    fontSize: '0.68rem',
                    textTransform: 'none',
                    borderColor: isSelected ? statusColor : 'divider',
                    backgroundColor: isSelected ? statusColor : 'transparent',
                    color: isSelected ? '#fff' : 'text.secondary',
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {statusLabel}
                </Button>
              )
            })}
          </Box>
        </Box>

        {/* Bloc: Modalități (2 rânduri) */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0.5,
            minHeight: 62,
            overflow: 'hidden',
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 0.25, color: 'text.secondary' }}>
            Modalități
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              maxHeight: 48, // 2 rânduri
              overflow: 'hidden',
            }}
          >
            {allModalities.map((modality) => {
              const isSelected = selectedModalities.includes(modality)
              return (
                <Button
                  key={modality}
                  size="small"
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={() => onModalityToggle(modality)}
                  sx={{
                    minWidth: 28,
                    height: 22,
                    px: 0.5,
                    py: 0,
                    fontSize: '0.68rem',
                    textTransform: 'none',
                    borderColor: isSelected ? '#0288d1' : 'divider',
                    backgroundColor: isSelected ? '#0288d1' : 'transparent',
                    color: isSelected ? '#fff' : 'text.secondary',
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {modality}
                </Button>
              )
            })}
          </Box>
        </Box>

        {/* Bloc: Adaugă Pacient - doar pentru admin/operator */}
        {canAddPatient && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 0.5,
              minHeight: 62,
              width: 240,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Tooltip title="Adaugă pacient nou în sistem">
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<PersonAddIcon sx={{ fontSize: 16 }} />}
                onClick={onAddPatient}
                sx={{
                  height: 28,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                }}
              >
                Adaugă Pacient
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Search Bar with Action Buttons */}
      <Box sx={{ mt: 0.6, display: 'flex', gap: 0.75, alignItems: 'center' }}>
        {/* Share Selected Button */}
        {canShare && (
          <Tooltip title={selectedCount > 0 ? `Share ${selectedCount} studii` : 'Share studii selectate'}>
            <span>
              <IconButton
                color="primary"
                onClick={onShare}
                disabled={selectedCount === 0}
                sx={{
                  width: 30,
                  height: 30,
                  border: '2px solid',
                  borderColor: selectedCount > 0 ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                  '&.Mui-disabled': {
                    borderColor: 'divider',
                    opacity: 0.5,
                  },
                }}
              >
                <ShareIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Download Selected Button - Icon only */}
        {canDownload && (
          <Tooltip title={selectedCount > 0 ? `Descarcă ${selectedCount} studii` : 'Descarcă studii selectate'}>
            <span>
              <IconButton
                color="success"
                onClick={onDownloadSelected}
                disabled={selectedCount === 0}
                sx={{
                  width: 30,
                  height: 30,
                  border: '2px solid',
                  borderColor: selectedCount > 0 ? 'success.main' : 'divider',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(46, 125, 50, 0.08)',
                  },
                  '&.Mui-disabled': {
                    borderColor: 'divider',
                    opacity: 0.5,
                  },
                }}
              >
                <DownloadIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Upload DICOM Button - Icon only, next to download */}
        {showQuickActions && (
          <Tooltip title="Încarcă fișiere DICOM externe">
            <IconButton
              color="primary"
              onClick={onUploadDicom}
              sx={{
                width: 30,
                height: 30,
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                },
              }}
            >
              <UploadIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Search Bar */}
        <TextField
          fullWidth
          size="small"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Căutare (Nume/ID/Data naștere/Nr. acces/Descriere studiu)..."
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.825rem',
              backgroundColor: '#1a202c',
            },
            '& .MuiInputBase-input': {
              py: 0.25,
              px: 1.25,
              color: '#a0aec0',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4a5568',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#718096',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3b82f6',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#a0aec0' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Paper>
  )
}

export default FiltersBar
